import React, { useState } from 'react';
import { ReactComponent as ChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/ChatbotReportsIcon.svg";
import { ReactComponent as CautionIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/CautionResponseChatbotReportsIcon.svg";
import { ReactComponent as ThumbsUpIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/InsightsSvgs/ThumbsUpChatbotReportsIcon.svg";
import { ReactComponent as ThumbsDownIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/InsightsSvgs/ThumbsDownChatbotReportsIcon.svg";
import { ReactComponent as InsightsIconChatbotReportsIcon } from "../../../../assets/ChatbotReportsSvgs/InsightsSvgs/InsightsChatbotReportsIcon.svg";
import { ReactComponent as MarkedHelpfulIcon } from "../../../../assets/ChatbotReportsSvgs/InsightsSvgs/MarkedHelpfulIcon.svg";
// import { ReactComponent as MarkedUnhelpfulIcon } from "../../../../assets/ChatbotReportsSvgs/InsightsSvgs/MarkedUnhelpfulChatbotReportsIcon.svg";

// import { InsightsDataInterface } from 'interface/ReportsChatbotInterface';
import InsightsMessageBubbleLoader from './InsightsMessageBubbleLoader';
import './style.scss';
import { InsightsDataInterface } from '../../../../interface/ReportsChatbotInterface';

interface InsightsMessageBubbleProps {
    sender?: 'user' | 'ai';
    insightsData: InsightsDataInterface;
    userInitial?: string;
    chatbotName?: string;
    isLoadingInsightsData?: boolean;
    isFailureInsightsData?: boolean;
    apiFailureResponeInsightsData?: string;
    onRetry?: () => void;
    timeLabel?: string;
    onThumbsUp?: () => void;
    onThumbsDown?: () => void;
}

/** Removes currency symbols that appear after a thousands separator or before a percentage */
function sanitizeInsightText(text: string): string {
    // Fix: $994,$178.70 → $994,178.70  (removes $ after comma within a number)
    let out = text.replace(/(\d),\$(\d)/g, '$1,$2');
    // Fix: $414,452.04% → 414,452.04%  (removes $ before a numeric percentage)
    out = out.replace(/\$([\d,]+\.?\d*%)/g, '$1');
    return out;
}

/** Formats the ISO timestamp returned by /generate_insight into a short local time label (e.g. "11:08 AM") */
function formatInsightsTimestamp(isoString?: string): string {
    if (!isoString) return '';
    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Renders insight text with **bold** segments as <strong> */
function renderInsightWithBold(insight: string) {
    const parts = sanitizeInsightText(insight).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        const match = part.match(/^\*\*(.+)\*\*$/);
        if (match) return <strong key={i}>{match[1]}</strong>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

const InsightsMessageBubble: React.FC<InsightsMessageBubbleProps> = ({
    sender: senderProp,
    chatbotName = 'Maghil Bot',
    insightsData,
    isLoadingInsightsData = false,
    isFailureInsightsData,
    apiFailureResponeInsightsData,
    userInitial = '',
    onRetry,
    timeLabel = '',
    onThumbsUp,
    onThumbsDown
}) => {
    const sender = senderProp ?? 'ai';
    const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

    if (isLoadingInsightsData) {
        return <InsightsMessageBubbleLoader chatbotName={chatbotName} timeLabel={timeLabel} />;
    }

    const isFailure = isFailureInsightsData ?? false;
    const displayText = isFailure ? (apiFailureResponeInsightsData ?? 'Something went wrong.') : (insightsData?.insight ?? '');
    // Prefer the API-supplied bubble timestamp; fall back to the caller-provided label
    const displayTimeLabel = formatInsightsTimestamp(insightsData?.insights_message_bubble_timestamp) || timeLabel;

    const handleThumbsUp = () => {
        if (feedback !== 'up') {
            setFeedback('up');
            onThumbsUp?.();
        }
    };

    const handleThumbsDown = () => {
        if (feedback !== 'down') {
            setFeedback('down');
            onThumbsDown?.();
        }
    };

    return (
        <div className={`insights-message-row ${sender}-message`}>
            {/* {sender === 'ai' && (
                <div className="insights-ai-avatar">
                    <ChatbotReportsIcon />
                </div>
            )}
            {sender === 'user' && (
                <div className="insights-user-avatar">{userInitial}</div>
            )} */}
            <div className="insights-message-wrapper">
                <div className="insights-message-bubble">
                    {isFailure ? (
                        <>
                            <CautionIconChatbotReportsIcon className="insights-caution-icon" />
                            <span className="insights-failure-text">{displayText}</span>
                            {onRetry && (
                                <button className="insights-retry-btn" type="button" onClick={onRetry}>
                                    Retry
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <InsightsIconChatbotReportsIcon className="insights-lightbulb-icon" />
                            <span className="insights-insight-text">
                                {renderInsightWithBold(displayText)}
                            </span>
                        </>
                    )}
                </div>
                {sender === 'ai' && (
                    <div className="insights-message-footer">
                        <span className="insights-footer-meta">
                            {chatbotName} • {displayTimeLabel}
                        </span>
                        {!isFailure && (
                            <div className="insights-feedback-icons">
                                {feedback === null ? (
                                    <>
                                        <button
                                            type="button"
                                            className="insights-feedback-btn"
                                            onClick={handleThumbsUp}
                                            aria-label="Thumbs up"
                                        >
                                            <ThumbsUpIconChatbotReportsIcon />
                                        </button>
                                        <button
                                            type="button"
                                            className="insights-feedback-btn"
                                            onClick={handleThumbsDown}
                                            aria-label="Thumbs down"
                                        >
                                            <ThumbsDownIconChatbotReportsIcon />
                                        </button>
                                    </>
                                ) : (
                                    <div className="insights-feedback-marked">
                                        <MarkedHelpfulIcon className="insights-marked-check-icon" />
                                        <span className="insights-marked-label">
                                            {feedback === 'up' ? 'Marked helpful' : 'Marked Unhelpful'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InsightsMessageBubble;
